import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment
} from '@mui/material';
import {
  ArrowBack,
  Storage,
  Lock,
  Backup,
  Refresh,
  Security,
  VpnKey,
  Restore,
  CheckCircle,
  ErrorOutline,
  Cloud,
  Download
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const DatabaseBackupPage: React.FC = () => {
  const navigate = useNavigate();
  const [pinVerified, setPinVerified] = useState(false);
  const [pin, setPin] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [loadingVerify, setLoadingVerify] = useState(false);

  // Dashboard states
  const [backupHistory, setBackupHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [backupInProgress, setBackupInProgress] = useState(false);

  // Restore states
  const [selectedBackup, setSelectedBackup] = useState<any | null>(null);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [restoreConfirmText, setRestoreConfirmText] = useState('');
  const [restoreError, setRestoreError] = useState('');
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [restoreSuccessMsg, setRestoreSuccessMsg] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // PIN Update states
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [pinUpdateError, setPinUpdateError] = useState('');
  const [pinUpdateSuccess, setPinUpdateSuccess] = useState('');
  const [loadingPinUpdate, setLoadingPinUpdate] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const backupPin = sessionStorage.getItem('backupPin') || pin;
    return {
      'Authorization': `Bearer ${token}`,
      'x-backup-pin': backupPin
    };
  };

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin) return;

    setLoadingVerify(true);
    setVerifyError('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_BASE_URL}/api/backup/verify-pin`, 
        { pin },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        sessionStorage.setItem('backupPin', pin);
        setPinVerified(true);
        fetchBackupHistory();
      }
    } catch (err: any) {
      console.error('PIN verify error:', err);
      setVerifyError(err.response?.data?.message || 'Geçersiz PIN kodu.');
    } finally {
      setLoadingVerify(false);
    }
  };

  const fetchBackupHistory = async () => {
    setLoadingHistory(true);
    setHistoryError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/backup/history`, {
        headers: getHeaders()
      });
      if (response.data.success) {
        setBackupHistory(response.data.data);
      }
    } catch (err: any) {
      console.error('Fetch backup history error:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        // PIN validation failed, lock the screen again
        setPinVerified(false);
        sessionStorage.removeItem('backupPin');
        setVerifyError('Oturum zaman aşımına uğradı veya PIN geçersiz.');
      } else {
        setHistoryError(err.response?.data?.message || 'Geçmiş yedekler alınamadı.');
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleTriggerBackup = async () => {
    setBackupInProgress(true);
    setHistoryError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/api/backup/run`, {}, {
        headers: getHeaders()
      });
      if (response.data.success) {
        // Refresh history immediately (it will show the backup in RUNNING state)
        fetchBackupHistory();
      }
    } catch (err: any) {
      console.error('Trigger backup error:', err);
      setHistoryError(err.response?.data?.message || 'Yedekleme başlatılamadı.');
    } finally {
      setBackupInProgress(false);
    }
  };

  const handleOpenRestore = (backup: any) => {
    setSelectedBackup(backup);
    setOpenRestoreDialog(true);
    setRestoreConfirmText('');
    setRestoreError('');
    setRestoreSuccessMsg('');
  };

  const handleCloseRestore = () => {
    if (restoreInProgress) return;
    setOpenRestoreDialog(false);
    setSelectedBackup(null);
  };

  const handleRestoreBackup = async () => {
    if (restoreConfirmText !== 'GERİ YÜKLE' || !selectedBackup) return;

    setRestoreInProgress(true);
    setRestoreError('');
    setRestoreSuccessMsg('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/backup/restore`, 
        { backupPath: selectedBackup.backupPath },
        { headers: getHeaders() }
      );

      if (response.data.success) {
        setRestoreSuccessMsg('Geri yükleme işlemi başarıyla başlatıldı. Arka planda tamamlanacaktır.');
        setTimeout(() => {
          handleCloseRestore();
        }, 3000);
      }
    } catch (err: any) {
      console.error('Restore error:', err);
      setRestoreError(err.response?.data?.message || 'Geri yükleme işlemi başlatılamadı.');
    } finally {
      setRestoreInProgress(false);
    }
  };

  const handleDownloadBackup = async (backup: any) => {
    setDownloadingId(backup.id);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/backup/download`, 
        { backupPath: backup.backupPath },
        { 
          headers: getHeaders(),
          responseType: 'blob' 
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const seconds = backup.startTime._seconds !== undefined ? backup.startTime._seconds : backup.startTime.seconds;
      const dateStr = seconds !== undefined ? new Date(seconds * 1000).toISOString().split('T')[0] : 'date';
      const filename = `sobcof-backup-${backup.type.toLowerCase()}-${dateStr}.zip`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Download error:', err);
      alert('Yedek indirilemedi. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleUpdatePin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPin || !newPin || !confirmNewPin) {
      setPinUpdateError('Tüm alanlar gereklidir.');
      return;
    }

    if (newPin.length < 4) {
      setPinUpdateError('Yeni PIN en az 4 karakter olmalıdır.');
      return;
    }

    if (newPin !== confirmNewPin) {
      setPinUpdateError('Yeni PIN kodları eşleşmiyor.');
      return;
    }

    setLoadingPinUpdate(true);
    setPinUpdateError('');
    setPinUpdateSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_BASE_URL}/api/backup/update-pin`, 
        { newPin },
        { 
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-backup-pin': currentPin
          }
        }
      );

      if (response.data.success) {
        setPinUpdateSuccess('Güvenlik PIN kodu başarıyla güncellendi.');
        sessionStorage.setItem('backupPin', newPin);
        setPin(newPin);
        setCurrentPin('');
        setNewPin('');
        setConfirmNewPin('');
      }
    } catch (err: any) {
      console.error('PIN update error:', err);
      setPinUpdateError(err.response?.data?.message || 'PIN güncellenemedi. Mevcut PIN yanlış olabilir.');
    } finally {
      setLoadingPinUpdate(false);
    }
  };

  // Check if session already holds verified PIN
  useEffect(() => {
    const savedPin = sessionStorage.getItem('backupPin');
    if (savedPin) {
      setPinVerified(true);
      setPin(savedPin);
    }
  }, []);

  useEffect(() => {
    if (pinVerified) {
      fetchBackupHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinVerified]);

  // Lock screen view
  if (!pinVerified) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        p: 2
      }}>
        <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Avatar sx={{ width: 60, height: 60, bgcolor: 'rgba(139, 69, 19, 0.1)', mx: 'auto', mb: 2 }}>
              <Lock sx={{ color: '#8B4513', fontSize: 30 }} />
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#2d3748' }}>
              Güvenli Yedekleme Paneli
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Kritik veritabanı işlemlerine erişmek için güvenlik PIN kodunuzu girmelisiniz.
            </Typography>

            {verifyError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {verifyError}
              </Alert>
            )}

            <form onSubmit={handleVerifyPin}>
              <TextField
                fullWidth
                type="password"
                label="Güvenlik PIN Kodu"
                variant="outlined"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                autoFocus
                placeholder="••••••"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VpnKey sx={{ color: '#a0aec0' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loadingVerify || !pin}
                sx={{
                  bgcolor: '#8B4513',
                  '&:hover': { bgcolor: '#70370f' },
                  borderRadius: 2,
                  py: 1.5,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(139, 69, 19, 0.2)'
                }}
              >
                {loadingVerify ? <CircularProgress size={24} color="inherit" /> : 'Giriş Yap'}
              </Button>
            </form>

            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/admin/dashboard')}
              startIcon={<ArrowBack />}
              sx={{ mt: 2, color: '#718096', fontWeight: 500 }}
            >
              Panele Geri Dön
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Helper to format Firestore Timestamp/JSON dates safely
  const formatBackupDate = (dateObj: any) => {
    if (!dateObj) return 'Yedek yok';
    const seconds = dateObj._seconds !== undefined ? dateObj._seconds : dateObj.seconds;
    if (seconds !== undefined) {
      return new Date(seconds * 1000).toLocaleString('tr-TR');
    }
    const d = new Date(dateObj);
    return isNaN(d.getTime()) ? 'Geçersiz Tarih' : d.toLocaleString('tr-TR');
  };

  // Calculate statistics
  const successBackups = backupHistory.filter(b => b.status === 'SUCCESS');
  const lastSuccessBackup = successBackups.length > 0 ? formatBackupDate(successBackups[0].startTime) : 'Yedek yok';
  const totalBackupsCount = backupHistory.length;

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 40, height: 40 }}>
            <Storage sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'white' }}>
            Veritabanı Yedekleme Yönetimi
          </Typography>
          <IconButton color="inherit" onClick={fetchBackupHistory} disabled={loadingHistory}>
            <Refresh />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {historyError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setHistoryError('')}>
            {historyError}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(76, 175, 80, 0.1)', mr: 2, width: 50, height: 50 }}>
                  <CheckCircle sx={{ color: '#4CAF50', fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">Son Başarılı Yedekleme</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>{lastSuccessBackup}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(139, 69, 19, 0.1)', mr: 2, width: 50, height: 50 }}>
                  <Backup sx={{ color: '#8B4513', fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">Toplam Yedek Sayısı</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>{totalBackupsCount} adet</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                <Avatar sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', mr: 2, width: 50, height: 50 }}>
                  <Cloud sx={{ color: '#2196F3', fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">Depolama Lokasyonu</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>Google Cloud Storage</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>
            Yedekleme Geçmişi
          </Typography>
          <Button
            variant="contained"
            startIcon={<Backup />}
            disabled={backupInProgress}
            onClick={handleTriggerBackup}
            sx={{
              bgcolor: '#8B4513',
              '&:hover': { bgcolor: '#70370f' },
              borderRadius: 2,
              px: 3,
              fontWeight: 600
            }}
          >
            {backupInProgress ? <CircularProgress size={20} color="inherit" /> : 'Şimdi Yedek Al (Anlık)'}
          </Button>
        </Box>

        {/* Backup Table */}
        <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)', mb: 4 }}>
          {loadingHistory && backupHistory.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Başlatan</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Koleksiyon Sayısı</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Yedekleme Türü</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                    <TableCell sx={{ fontWeight: 600, align: 'right' }}>Aksiyon</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backupHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Henüz hiçbir veritabanı yedeği bulunmuyor.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    backupHistory.map((backup) => (
                      <TableRow key={backup.id} sx={{ '&:hover': { bgcolor: '#fcfaf9' } }}>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {formatBackupDate(backup.startTime)}
                        </TableCell>
                        <TableCell>{backup.initiator}</TableCell>
                        <TableCell>{backup.collectionsCount || 0} koleksiyon</TableCell>
                        <TableCell>
                          <Chip
                            label={backup.type === 'AUTO' ? 'Otomatik' : 'Manuel'}
                            variant="outlined"
                            size="small"
                            sx={{
                              borderColor: backup.type === 'AUTO' ? '#2196F3' : '#8B4513',
                              color: backup.type === 'AUTO' ? '#2196F3' : '#8B4513',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              backup.status === 'SUCCESS' ? 'Başarılı' :
                              backup.status === 'RUNNING' ? 'Çalışıyor' : 'Başarısız'
                            }
                            size="small"
                            color={
                              backup.status === 'SUCCESS' ? 'success' :
                              backup.status === 'RUNNING' ? 'warning' : 'error'
                            }
                            sx={{ fontWeight: 600 }}
                          />
                          {backup.error && (
                            <Typography variant="caption" display="block" color="error" sx={{ maxWidth: 200, mt: 0.5, fontSize: '10px' }} noWrap>
                              {backup.error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Restore />}
                              disabled={backup.status !== 'SUCCESS' || restoreInProgress}
                              onClick={() => handleOpenRestore(backup)}
                              sx={{
                                borderColor: '#D2691E',
                                color: '#D2691E',
                                '&:hover': {
                                  borderColor: '#b55a18',
                                  bgcolor: 'rgba(210, 105, 30, 0.04)'
                                },
                                fontWeight: 600
                              }}
                            >
                              Geri Yükle
                            </Button>

                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={downloadingId === backup.id ? <CircularProgress size={16} /> : <Download />}
                              disabled={backup.status !== 'SUCCESS' || downloadingId !== null}
                              onClick={() => handleDownloadBackup(backup)}
                              sx={{
                                borderColor: '#2196F3',
                                color: '#2196F3',
                                '&:hover': {
                                  borderColor: '#1976D2',
                                  bgcolor: 'rgba(33, 150, 243, 0.04)'
                                },
                                fontWeight: 600
                              }}
                            >
                              İndir
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Security configuration card */}
        <Grid container spacing={3} justifyContent="center">
          <Grid size={{ xs: 12, md: 8, lg: 6 }}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'rgba(139, 69, 19, 0.1)', mr: 2 }}>
                    <Security sx={{ color: '#8B4513' }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>
                    Yedekleme Güvenlik Ayarları
                  </Typography>
                </Box>

                {pinUpdateSuccess && (
                  <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                    {pinUpdateSuccess}
                  </Alert>
                )}

                {pinUpdateError && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {pinUpdateError}
                  </Alert>
                )}

                <form onSubmit={handleUpdatePin}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Mevcut Güvenlik PIN Kodu"
                    variant="outlined"
                    size="small"
                    value={currentPin}
                    onChange={(e) => setCurrentPin(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    type="password"
                    label="Yeni Güvenlik PIN Kodu"
                    variant="outlined"
                    size="small"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    type="password"
                    label="Yeni PIN Kodu (Tekrar)"
                    variant="outlined"
                    size="small"
                    value={confirmNewPin}
                    onChange={(e) => setConfirmNewPin(e.target.value)}
                    sx={{ mb: 3 }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loadingPinUpdate || !currentPin || !newPin}
                    sx={{
                      bgcolor: '#8B4513',
                      '&:hover': { bgcolor: '#70370f' },
                      borderRadius: 2,
                      fontWeight: 600
                    }}
                  >
                    PIN Kodunu Güncelle
                  </Button>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={openRestoreDialog}
        onClose={handleCloseRestore}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 3 }}>
          <ErrorOutline color="warning" sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Veritabanını Geri Yükle</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Seçtiğiniz yedek dosyasını geri yüklemek üzeresiniz. Bu işlem veritabanınızdaki tüm mevcut verilerin üzerine yazacaktır (user_swipes hariç).
          </DialogContentText>
          <DialogContentText sx={{ mb: 3, color: '#e53e3e', fontWeight: 600 }}>
            Uygulamanın aktif durumunu bozmamak için bu işlemi yalnızca gerektiğinde çalıştırın!
          </DialogContentText>

          {restoreError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {restoreError}
            </Alert>
          )}

          {restoreSuccessMsg && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {restoreSuccessMsg}
            </Alert>
          )}

          <TextField
            fullWidth
            label='Devam etmek için "GERİ YÜKLE" yazın'
            variant="outlined"
            size="small"
            value={restoreConfirmText}
            onChange={(e) => setRestoreConfirmText(e.target.value)}
            disabled={restoreInProgress}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseRestore} disabled={restoreInProgress} color="inherit" sx={{ fontWeight: 600 }}>
            İptal
          </Button>
          <Button
            onClick={handleRestoreBackup}
            disabled={restoreConfirmText !== 'GERİ YÜKLE' || restoreInProgress}
            variant="contained"
            color="error"
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(229, 62, 62, 0.2)'
            }}
          >
            {restoreInProgress ? <CircularProgress size={20} color="inherit" /> : 'Geri Yükle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseBackupPage;
